import { Controller } from "@hotwired/stimulus"

// 옵션 그룹/선택지 편집을 클라이언트에서 pending 상태로 관리하고
// "완료" 클릭 시 batch_save로 일괄 저장, "닫기" 클릭 시 폐기 confirm.
export default class extends Controller {
  static values = { brandId: Number }

  groupCreates   = []
  groupDestroys  = new Set()
  choiceCreates  = []
  choiceDestroys = []
  tempCounter    = 0

  get isDirty() {
    return this.groupCreates.length > 0 ||
           this.groupDestroys.size > 0 ||
           this.choiceCreates.length > 0 ||
           this.choiceDestroys.length > 0
  }

  // ── 그룹 추가 ──────────────────────────────────────────────

  addGroup(event) {
    event.preventDefault()
    const form = event.target
    const name = form.querySelector('[name="name"]').value.trim()
    const type = form.querySelector('[name="selection_type"]').value
    const req  = form.querySelector('[name="required"]')?.checked ?? false
    if (!name) return

    const tempId = `temp-group-${++this.tempCounter}`
    this.groupCreates.push({ tempId, name, selection_type: type, required: req })
    document.getElementById("option_group_list")
            ?.insertAdjacentHTML("beforeend", this.buildGroupRow(tempId, name, type, req))
    form.reset()
  }

  // ── 그룹 삭제 ──────────────────────────────────────────────

  deleteGroup(event) {
    event.preventDefault()
    const { groupId, groupName } = event.currentTarget.dataset
    if (!confirm(`"${groupName}" 그룹을 삭제하시겠습니까?`)) return

    if (groupId.startsWith("temp-group-")) {
      this.groupCreates = this.groupCreates.filter(g => g.tempId !== groupId)
    } else {
      this.groupDestroys.add(groupId)
      this.choiceCreates = this.choiceCreates.filter(c => c.group_id !== groupId)
    }
    document.getElementById(`group-row-${groupId}`)?.remove()
  }

  // ── 선택지 추가 ────────────────────────────────────────────

  addChoice(event) {
    event.preventDefault()
    const form       = event.target
    const groupId    = form.dataset.groupId
    const name       = form.querySelector('[name="name"]').value.trim()
    const priceDelta = parseInt(form.querySelector('[name="price_delta"]').value.replace(/,/g, '')) || 0
    if (!name) return

    const tempId = `temp-choice-${++this.tempCounter}`
    this.choiceCreates.push({ tempId, group_id: groupId, name, price_delta: priceDelta })

    const tagsEl = document.getElementById(`choice-tags-${groupId}`)
    if (tagsEl) {
      tagsEl.querySelector("[data-no-choices]")?.remove()
      tagsEl.insertAdjacentHTML("beforeend", this.buildChoiceTag(groupId, tempId, name, priceDelta))
    }
    form.reset()
  }

  // ── 선택지 삭제 ────────────────────────────────────────────

  deleteChoice(event) {
    event.preventDefault()
    const { groupId, choiceId } = event.currentTarget.dataset

    if (choiceId.startsWith("temp-choice-")) {
      this.choiceCreates = this.choiceCreates.filter(c => c.tempId !== choiceId)
    } else {
      this.choiceDestroys.push({ group_id: groupId, choice_id: choiceId })
    }

    const tagEl    = document.getElementById(`choice-tag-${groupId}-${choiceId}`)
    const tagsEl   = document.getElementById(`choice-tags-${groupId}`)
    tagEl?.remove()

    if (tagsEl && !tagsEl.querySelector('[id^="choice-tag-"]')) {
      tagsEl.insertAdjacentHTML("beforeend",
        '<span data-no-choices class="text-default-slate/40">선택지 없음</span>')
    }
  }

  // ── 금액 입력 포맷 ─────────────────────────────────────────

  formatPriceInput(event) {
    const input = event.target
    const raw = input.value.replace(/[^0-9]/g, '')
    const num = Math.min(parseInt(raw) || 0, 99999999)
    input.value = num === 0 ? '0' : num.toLocaleString('ko-KR')
  }

  // ── 완료: 일괄 저장 ────────────────────────────────────────

  async save() {
    if (!confirm("저장하시겠습니까?")) return
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const body = {
      brand_id:        this.brandIdValue,
      group_creates:   this.groupCreates.map(({ tempId, name, selection_type, required }) => ({ temp_id: tempId, name, selection_type, required })),
      group_destroys:  [...this.groupDestroys],
      choice_creates:  this.choiceCreates.map(({ group_id, name, price_delta }) => ({ group_id, name, price_delta })),
      choice_destroys: this.choiceDestroys
    }

    try {
      const response = await fetch("/option_groups/batch_save", {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrf,
          "Accept":       "text/vnd.turbo-stream.html"
        },
        body: JSON.stringify(body)
      })
      if (response.ok) {
        const html = await response.text()
        window.Turbo.renderStreamMessage(html)
        window.dispatchEvent(new CustomEvent("modals:close"))
      }
    } catch (e) {
      console.error("Option groups batch save failed:", e)
    }
  }

  // ── 닫기: 미저장 시 confirm ────────────────────────────────

  cancel() {
    if (this.isDirty && !confirm("저장하지 않으면 변경사항이 삭제됩니다.\n닫으시겠습니까?")) return
    window.dispatchEvent(new CustomEvent("modals:close"))
  }

  // ── DOM 빌더 ───────────────────────────────────────────────

  buildGroupRow(id, name, selectionType, required) {
    const isMulti = selectionType === "multi"
    const reqBadge = required
      ? `<span class="px-2.5 py-0.5 rounded-full bg-danger/10 text-danger">필수</span>`
      : ""
    return `
      <div class="border border-table-border rounded-xl p-4" id="group-row-${id}">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <span class="font-bold text-default-slate">${name}</span>
            <span class="px-2.5 py-0.5 rounded-full bg-default-slate/10 text-default-slate">
              ${isMulti ? "다중 선택" : "단일 선택"}
            </span>
            ${reqBadge}
          </div>
          <button type="button"
                  data-action="option-groups-manager#deleteGroup"
                  data-group-id="${id}" data-group-name="${name}"
                  class="w-8 h-8 flex items-center justify-center border border-danger/50 rounded-full text-danger hover:bg-danger/5 flex-shrink-0">
            <i class="fa-solid fa-trash text-sm"></i>
          </button>
        </div>
        <div class="flex flex-wrap gap-2 mt-3" id="choice-tags-${id}">
          <span data-no-choices class="text-default-slate/40">선택지 없음</span>
        </div>
        <form data-action="submit->option-groups-manager#addChoice" data-group-id="${id}"
              class="flex items-center gap-2 mt-3">
          <input type="text" name="name" placeholder="선택지 이름" required
                 class="flex-1 min-w-0 border border-table-border rounded-lg px-3 py-2">
          <input type="text" name="price_delta" value="0" placeholder="추가 금액"
                 inputmode="numeric" maxlength="10"
                 data-action="input->option-groups-manager#formatPriceInput"
                 class="w-36 border border-table-border rounded-lg px-3 py-2 text-right">
          <button type="submit" class="modal-pos-btn-small modal-pos-btn-border whitespace-nowrap">
            추가
          </button>
        </form>
      </div>`
  }

  buildChoiceTag(groupId, choiceId, name, priceDelta) {
    const priceHtml = priceDelta !== 0
      ? `<span class="text-default-slate/50">+${priceDelta.toLocaleString("ko-KR")}원</span>`
      : ""
    return `
      <div class="flex items-center gap-1.5 pl-3.5 pr-2 py-1.5 rounded-full border border-gray-200 bg-gray-50"
           id="choice-tag-${groupId}-${choiceId}">
        <span class="text-default-slate font-medium">${name}</span>
        ${priceHtml}
        <button type="button"
                data-action="option-groups-manager#deleteChoice"
                data-group-id="${groupId}" data-choice-id="${choiceId}"
                class="w-5 h-5 flex items-center justify-center rounded-full text-default-slate/40 hover:text-danger hover:bg-danger/10 flex-shrink-0 ml-0.5">
          <i class="fa-solid fa-xmark text-sm"></i>
        </button>
      </div>`
  }
}
